'use client'

import { useState } from 'react'
import { Accordion, Card, Cell, Container, Spacer, Text } from '@vinted/web-ui'
import { compact } from 'lodash'

import { ShowAtBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { List } from '@marketplace-web/common-components/list-ui'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import { ShippingDayModel } from '@marketplace-web/shipping/checkout-shipping-data'
import {
  clickEvent,
  DropOffPointCarrierModel,
  DropOffPointModel,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'
import { useDropOffPointMapContext } from '@marketplace-web/shipping-services-provider/shipping-services-provider-feature'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'

import DropOffPointInfoCard from './DropOffPointInfoCard'

type Props = {
  selectedDropOffPoint: DropOffPointModel | null
  dropOffPointCarrier?: DropOffPointCarrierModel | null
  isAdvancedDetailsOpen: boolean
  toggleSelectedPointDetailsState: () => void
}

const DropOffPointDetails = ({
  selectedDropOffPoint,
  dropOffPointCarrier,
  isAdvancedDetailsOpen,
  toggleSelectedPointDetailsState,
}: Props) => {
  const translate = useTranslate(
    'conversation.drop_off_point_modal.tablets_up.drop_off_point_details',
  )
  const { uiState, dropOffPointSource } = useDropOffPointMapContext()
  const transaction = useConversationContext().conversation?.transaction

  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false)

  const transactionId = transaction?.id
  const shipmentStatus = transaction?.shipment?.status

  const { track } = useTracking()

  const handleAdditionalDetailsToggle = (isExpanded: boolean) => {
    setIsAccordionExpanded(isExpanded)

    if (!selectedDropOffPoint?.code || !transactionId || !shipmentStatus || !dropOffPointSource)
      return

    const targetDetails = JSON.stringify({
      transaction_id: transactionId,
      drop_off_point_code: selectedDropOffPoint?.code,
      shipment_status: shipmentStatus,
      drop_off_points_source: dropOffPointSource,
    })

    track(
      clickEvent({
        screen: 'seller_drop_off_map',
        target: 'drop_off_point_details',
        targetDetails,
      }),
    )
  }

  const handleCarrierLinkClick = () => {
    if (!selectedDropOffPoint?.code || !transactionId) return

    const targetDetails = JSON.stringify({
      transaction_id: transactionId,
      drop_off_point_code: selectedDropOffPoint?.code,
    })

    track(
      clickEvent({
        screen: 'seller_drop_off_map',
        target: 'hyperlink',
        targetDetails,
      }),
    )
  }

  const renderCarrierLinkCell = () => (
    <Cell
      styling={Cell.Styling.Tight}
      body={
        <span role="none" onClick={handleCarrierLinkClick}>
          <Text text={dropOffPointCarrier?.locationMapNote} html as="span" />
        </span>
      }
    />
  )

  const renderBusinessHours = (shippingDay: ShippingDayModel) => {
    const hours = shippingDay.hours.map(({ opensAt, closesAt }) =>
      compact([opensAt, closesAt]).join('–'),
    )

    return (
      <div key={shippingDay.weekday} className="u-flexbox">
        <div className="u-flexbox u-flex-equal-size">
          <Text as="span" text={shippingDay.label} />
        </div>
        <div className="u-flexbox u-flex-equal-size u-justify-content-flex-end u-text-right">
          <Text as="span">
            {shippingDay.hours.length
              ? compact(hours).join('; ')
              : translate('selected_point_details.opening_hours_closed')}
          </Text>
        </div>
      </div>
    )
  }

  const renderSelectedPointDetails = () => {
    if (!selectedDropOffPoint) return null

    const { description, businessHours } = selectedDropOffPoint

    return (
      <>
        {description && (
          <>
            <Text
              as="span"
              theme="muted"
              text={translate('selected_point_details.description')}
              testId="drop-off-point-details-location-description"
            />
            <Spacer size={Spacer.Size.Large} />
            <Text as="span" text={description} />
            <Spacer size={Spacer.Size.Large} />
          </>
        )}
        {!!businessHours.length && (
          <div
            className="u-flexbox u-flex-direction-column"
            data-testid="drop-off-point-details-opening-hours"
          >
            <Text
              as="h3"
              theme="muted"
              type={Text.Type.Subtitle}
              text={translate('selected_point_details.opening_hours')}
            />
            <Spacer size={Spacer.Size.Large} />
            {businessHours.map(renderBusinessHours)}
          </div>
        )}
        <Spacer size={Spacer.Size.Large} />
        {renderCarrierLinkCell()}
        <Spacer />
      </>
    )
  }

  const renderAdditionalDetails = () => {
    return (
      <Accordion
        title={translate('additional_details.title')}
        content={renderSelectedPointDetails()}
        headerId="drop_off_points_additional_details_accordion_header"
        bodyId="drop_off_points_additional_details_accordion_body"
        isExpanded={isAccordionExpanded}
        onToggle={handleAdditionalDetailsToggle}
      />
    )
  }

  const renderTabletsUp = () => {
    if (uiState === UiState.Pending) return null
    if (!selectedDropOffPoint) return null

    return (
      <div className="drop-off-point-map-details">
        <Card styling={Card.Styling.Lifted} testId="drop-off-point-map-details">
          <List>
            <DropOffPointInfoCard
              dropOffPoint={selectedDropOffPoint}
              dropOffPointCarrier={dropOffPointCarrier}
            />
            {renderAdditionalDetails()}
          </List>
        </Card>
      </div>
    )
  }

  const renderPhonesEmptyState = () => (
    <Card styling={Card.Styling.Lifted}>
      <Container>
        <Text
          as="span"
          text={translate('empty_state')}
          alignment={Text.Alignment.Center}
          width={Text.Width.Parent}
        />
      </Container>
    </Card>
  )

  const renderAdvancedDetailsPhones = (selectedPoint: DropOffPointModel) => {
    if (!selectedPoint) return null

    return (
      <>
        <DropOffPointInfoCard
          dropOffPoint={selectedPoint}
          dropOffPointCarrier={dropOffPointCarrier}
          onClick={toggleSelectedPointDetailsState}
        />
        <div className="u-background-light">
          <Spacer size={Spacer.Size.Large} />
        </div>
        {isAdvancedDetailsOpen && (
          <div className="u-flexbox u-flex-direction-column">
            <Container>{renderSelectedPointDetails()}</Container>
          </div>
        )}
      </>
    )
  }

  const renderPhones = () => {
    if (uiState === UiState.Pending) return null
    if (uiState === UiState.Failure) return renderPhonesEmptyState()
    if (!selectedDropOffPoint) return null
    if (isAdvancedDetailsOpen) return renderAdvancedDetailsPhones(selectedDropOffPoint)

    return (
      <div className="drop-off-point-map-details">
        <Card styling={Card.Styling.Lifted} testId="drop-off-point-map-details">
          <DropOffPointInfoCard
            dropOffPoint={selectedDropOffPoint}
            dropOffPointCarrier={dropOffPointCarrier}
            onClick={toggleSelectedPointDetailsState}
            navigating
            chevron
          />
        </Card>
      </div>
    )
  }

  return (
    <>
      <ShowAtBreakpoint breakpoint="tabletsUp">{renderTabletsUp()}</ShowAtBreakpoint>
      <ShowAtBreakpoint breakpoint="phones">{renderPhones()}</ShowAtBreakpoint>
    </>
  )
}

export default DropOffPointDetails
